'use client'

import { PlusIcon, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { Button } from '@/components/ui/button'
import {
  type ConditionKind,
  type ConditionNode,
  createNode,
} from '@/features/order/order-form/lib/trigger-condition-model'
import {
  CONDITION_KIND_OPTIONS,
  CONDITION_KIND_TO_TRIGGER_TYPE,
} from '@/features/order/order-form/lib/trigger-select-options'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useUpgradeHint } from '@/hooks/use-upgrade-hint'
import { isTriggerConditionAllowed } from '@/lib/billing/capabilities'
import { cn } from '@/lib/utils'

import { TokenSelect, type TokenSelectOption, useConditionEditorData } from './field-controls'
import {
  ActionEventEditor,
  CandleEditor,
  DbNewsEditor,
  OptionIvEditor,
  PortfolioMetricEditor,
  PositionStatsEditor,
  PriceEditor,
  ReturnsEditor,
  RiskMetricEditor,
  ScheduleEditor,
  TechnicalIndicatorEditor,
} from './leaf-editors'

type SelectableKind = Exclude<ConditionKind, 'action_event'>

/**
 * Plan-gates the condition-kind picker (FREE = price only). Resolved ONCE at the
 * editor root and threaded into the recursive tree as a prop — calling it per
 * node would re-subscribe to the subscription/plans queries and re-run the
 * plan-catalog scan for every node. Locked kinds stay visible but disabled,
 * starred, and name the cheapest plan that unlocks them on hover. The current node's kind is
 * un-gated per-node by the editor (see `ConditionTreeEditor`).
 */
export function useConditionKindOptions(): TokenSelectOption<SelectableKind>[] {
  const t = useTranslations('orderForm.conditionEditor')
  const { isTriggerConditionAllowed: triggerAllowed } = useCapabilities()
  const upgradeHint = useUpgradeHint()

  return useMemo(
    () =>
      CONDITION_KIND_OPTIONS.map((o) => {
        const type = CONDITION_KIND_TO_TRIGGER_TYPE[o.value]
        const allowed = triggerAllowed(type)
        return {
          ...o,
          label: t(`conditionKinds.${o.value}`),
          disabled: !allowed,
          locked: !allowed,
          lockedPlan: allowed
            ? undefined
            : upgradeHint((caps) => isTriggerConditionAllowed(caps, type)),
        }
      }),
    [t, triggerAllowed, upgradeHint]
  )
}

/** True when any leaf in the subtree has user-entered content worth preserving. */
function hasAnyInput(node: ConditionNode): boolean {
  switch (node.kind) {
    case 'and':
    case 'or':
      return node.children.some(hasAnyInput)
    case 'price':
      return Boolean(node.symbol || node.value || node.relative)
    case 'technical_indicator':
      return Boolean(node.left.symbol || node.threshold || node.right.symbol)
    case 'returns':
      return Boolean(node.symbol || node.valuePercent)
    case 'candle':
      return Boolean(node.symbol || node.value)
    case 'portfolio_metric':
      return Boolean(node.value)
    case 'position_stats':
      return Boolean(node.scopeValue || node.value)
    case 'risk_metric':
      return Boolean(node.asset || node.value)
    case 'option_iv':
      return Boolean(node.symbol || node.underlying || node.valuePercent)
    case 'schedule':
      return Boolean(node.value)
    case 'db_news':
      return [node.eeType, node.newsType, node.coins, node.tags, node.textContains].some((g) =>
        g.some((s) => s.trim() !== '')
      )
    case 'action_event':
      return Boolean(node.targetValue)
  }
}

function LeafBody({
  node,
  onChange,
}: {
  node: Exclude<ConditionNode, { kind: 'and' } | { kind: 'or' }>
  onChange: (node: ConditionNode) => void
}) {
  switch (node.kind) {
    case 'price':
      return <PriceEditor node={node} onChange={onChange} />
    case 'technical_indicator':
      return <TechnicalIndicatorEditor node={node} onChange={onChange} />
    case 'returns':
      return <ReturnsEditor node={node} onChange={onChange} />
    case 'candle':
      return <CandleEditor node={node} onChange={onChange} />
    case 'portfolio_metric':
      return <PortfolioMetricEditor node={node} onChange={onChange} />
    case 'position_stats':
      return <PositionStatsEditor node={node} onChange={onChange} />
    case 'risk_metric':
      return <RiskMetricEditor node={node} onChange={onChange} />
    case 'option_iv':
      return <OptionIvEditor node={node} onChange={onChange} />
    case 'schedule':
      return <ScheduleEditor node={node} onChange={onChange} />
    case 'db_news':
      return <DbNewsEditor node={node} onChange={onChange} />
    case 'action_event':
      return <ActionEventEditor node={node} onChange={onChange} />
  }
}

export function ConditionTreeEditor({
  node,
  onChange,
  depth = 0,
  onRemove,
  kindOptions,
}: {
  node: ConditionNode
  onChange: (node: ConditionNode) => void
  depth?: number
  onRemove?: () => void
  kindOptions: TokenSelectOption<SelectableKind>[]
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const { defaultSymbol, defaultAccountId } = useConditionEditorData()
  const createDefaultNode = (kind: ConditionKind) =>
    createNode(kind, Date.now(), { symbol: defaultSymbol, accountId: defaultAccountId })

  // The currently-selected kind is never gated in this node: a locked kind can
  // stay as the existing value (e.g. after a downgrade), and Radix mirrors the
  // selected item into the closed trigger — a badge there would clutter it.
  const nodeKindOptions = useMemo(
    () =>
      kindOptions.map((o) =>
        o.value === node.kind ? { ...o, disabled: false, locked: false } : o
      ),
    [kindOptions, node.kind]
  )

  const convertKind = (next: SelectableKind) => {
    if (next === node.kind) return
    // The picker disables locked kinds, but guard the conversion too (a paying
    // user could downgrade mid-build with a richer condition already selected).
    if (kindOptions.find((o) => o.value === next)?.disabled) return
    // Preserve the stable client id across the conversion so React keys (and thus
    // editor focus / state) survive.
    const id = node.id
    const isGroupNext = next === 'and' || next === 'or'
    const isGroupNow = node.kind === 'and' || node.kind === 'or'
    if (isGroupNext) {
      if (isGroupNow) {
        onChange({ kind: next, children: node.children, id })
      } else {
        // Wrap the current leaf as the first child of the new group.
        onChange({ kind: next, children: [node, createDefaultNode('price')], id })
      }
      return
    }
    // Group → single leaf collapses N children into one; confirm before
    // discarding any user-entered conditions so it isn't a silent loss.
    if (isGroupNow && hasAnyInput(node) && !window.confirm(t('switchSingleConditionConfirm'))) {
      return
    }
    onChange({ ...createDefaultNode(next), id })
  }

  const header = (
    <div className="flex items-center gap-1.5">
      <div className="flex-1">
        {node.kind === 'action_event' ? (
          // action_event is internal/legacy and not user-selectable, so it has no
          // dropdown option — show a static label instead of a blank select.
          <div className="flex h-9 items-center bg-white/5 px-2.5 text-sm font-semibold text-white/70">
            {t('conditionKinds.action_event')}
          </div>
        ) : (
          <TokenSelect
            value={node.kind as SelectableKind}
            onChange={convertKind}
            options={nodeKindOptions}
          />
        )}
      </div>
      {onRemove && (
        <Button
          variant="ghost"
          size="icon"
          type="button"
          onClick={onRemove}
          className="shrink-0 p-1 text-white/30 hover:bg-white/5 hover:text-white/70"
          aria-label={t('removeCondition')}
        >
          <XIcon className="size-4" />
        </Button>
      )}
    </div>
  )

  if (node.kind === 'and' || node.kind === 'or') {
    const setChild = (i: number, child: ConditionNode) =>
      onChange({ ...node, children: node.children.map((c, idx) => (idx === i ? child : c)) })
    const removeChild = (i: number) =>
      onChange({ ...node, children: node.children.filter((_, idx) => idx !== i) })
    const addChild = () =>
      onChange({ ...node, children: [...node.children, createDefaultNode('price')] })

    return (
      <div className={cn('flex flex-col gap-2', depth > 0 && 'pl-2')}>
        {header}
        <p className="text-[10px] leading-tight text-white/40">
          {node.kind === 'and' ? t('andDescription') : t('orDescription')}
        </p>
        {node.children.map((child, i) => (
          <div key={child.id ?? i} className="bg-white/3 p-2">
            <ConditionTreeEditor
              node={child}
              onChange={(next) => setChild(i, next)}
              depth={depth + 1}
              onRemove={node.children.length > 2 ? () => removeChild(i) : undefined}
              kindOptions={kindOptions}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={addChild}
          className="flex h-9 items-center justify-center gap-1 bg-white/[0.03] text-xs font-medium text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white/80"
        >
          <PlusIcon className="size-3" /> {t('addCondition')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {header}
      <LeafBody node={node} onChange={onChange} />
    </div>
  )
}
